import type {
    ComponentStageData,
    MobileAlgoPlugin,
    MobileAlgoPluginInitializationArgs,
    PluginContext,
    PluginHeuristicsRegistry,
    PluginStageHandler
} from '../../types'
import {widthHeuristics, horizontalSpacingHeuristics, getAvailableWidthKey} from './heuristicsDefinitions'
import {findHeuristic, getParent, createAdaptiveHeuristic, updateTargetLayout} from '../common'
import type {GridContainerLayout, GridItemLayout, Pointer, UnitSize} from '@wix/document-services-types'
import {getTargetLayoutPointer} from '../../conversionDal/utils/pointer'
import type {LayoutEntryItem} from '../../conversionDal/types'
import type {HorizontalScaleAndSizePluginAPI} from './types'
import {SemanticTypes} from '../../preprocess/conversionDataBuilders'
import {deepClone} from '@wix/wix-immutable-proxy'
import _ from 'lodash'

export const createPlugin = ({stages, readOnlyExtensionAPI}: MobileAlgoPluginInitializationArgs): MobileAlgoPlugin => {
    const updateAvailableWidthByExistingLayout = (
        {componentPointer, conversionDal, config}: ComponentStageData,
        contextHelper?: PluginContext
    ) => {
        const availableWidthKey = getAvailableWidthKey(componentPointer)

        const parentPointer = getParent(conversionDal, componentPointer)
        const isBoxPlugin = conversionDal.get(componentPointer).semanticType === SemanticTypes.BOX_PLUGIN

        if (!parentPointer) {
            return contextHelper!.set(availableWidthKey, config.targetCanvasSize)
        }

        const availableWidth = isBoxPlugin
            ? config.targetCanvasSize
            : contextHelper!.get(getAvailableWidthKey(parentPointer))

        const {componentLayout, itemLayout} = (conversionDal.get(getTargetLayoutPointer(componentPointer)) ??
            {}) as LayoutEntryItem
        const {containerLayout} = (conversionDal.get(getTargetLayoutPointer(parentPointer)) ?? {}) as LayoutEntryItem

        const calculateColumnsRatio = () => {
            if (
                !(itemLayout as GridItemLayout)?.gridArea?.columnStart ||
                !(itemLayout as GridItemLayout)?.gridArea?.columnEnd ||
                !(containerLayout as GridContainerLayout)?.columns?.length
            ) {
                return 1
            }

            return (
                ((itemLayout as GridItemLayout).gridArea.columnEnd -
                    (itemLayout as GridItemLayout).gridArea.columnStart) /
                (containerLayout as GridContainerLayout).columns.length
            )
        }

        const currentWidth = (componentLayout?.width as UnitSize)?.value ?? 100
        contextHelper!.set(availableWidthKey, (currentWidth / 100) * (availableWidth / calculateColumnsRatio()))
    }

    const setContainerColumns: PluginStageHandler<ComponentStageData> = (componentStageData: ComponentStageData) => {
        const {componentPointer, conversionDal} = componentStageData
        const component = conversionDal.get(componentPointer)

        if (component.type !== 'Container' || component.isSlottedComponent) {
            return
        }

        const {containerLayout} = deepClone(conversionDal.get(getTargetLayoutPointer(componentPointer))) ?? {}

        updateTargetLayout(componentStageData, {
            containerLayout: {
                ...containerLayout,
                columns: [
                    {
                        type: 'MinMaxSize',
                        min: {type: 'px', value: 0},
                        max: {type: 'fr', value: 1}
                    }
                ],
                type: 'GridContainerLayout'
            }
        })

        return {
            appliedHeuristics: ['SINGLE COLUMN LAYOUT']
        }
    }

    const setContainerColumnsAdaptive: PluginStageHandler<ComponentStageData> = (
        componentStageData: ComponentStageData,
        ctx: PluginContext
    ) => {
        const {initialSnapshot, componentPointer} = componentStageData
        const targetLayout = initialSnapshot.getValue(getTargetLayoutPointer(componentPointer))

        if (_.isEmpty(targetLayout?.containerLayout)) {
            return setContainerColumns(componentStageData, ctx)
        }
    }

    const run: PluginStageHandler<ComponentStageData> = (
        componentStageData: ComponentStageData,
        contextHelper?: PluginContext
    ) => {
        const marginsHeuristicToApply = findHeuristic(horizontalSpacingHeuristics, componentStageData, contextHelper)
        marginsHeuristicToApply.apply(componentStageData, contextHelper)
        const widthHeuristicToApply = findHeuristic(
            widthHeuristics,
            componentStageData,
            contextHelper,
            readOnlyExtensionAPI
        )
        widthHeuristicToApply.apply(componentStageData, contextHelper)

        return {
            appliedHeuristics: [marginsHeuristicToApply.heuristicCode, widthHeuristicToApply.heuristicCode]
        }
    }

    const register = (registry: PluginHeuristicsRegistry) => {
        registry.register(
            stages.HORIZONTAL_LAYOUT_SCALE,
            createAdaptiveHeuristic(run, updateAvailableWidthByExistingLayout)
        )
        registry.register(
            stages.HORIZONTAL_LAYOUT_SCALE,
            createAdaptiveHeuristic(setContainerColumns, setContainerColumnsAdaptive)
        )
    }

    const createApi = (context: PluginContext): HorizontalScaleAndSizePluginAPI => {
        return {
            getWidth: (compPointer: Pointer): number => {
                return context.get(getAvailableWidthKey(compPointer))
            }
        }
    }

    const name = 'horizontalScaleAndSize'

    return {register, createApi, name, dependencies: ['verticalScaleAndSize']}
}
